"use client";
import React, { useEffect, useState } from "react";
import {
  Container,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  Card,
  Button,
} from "react-bootstrap";
import { FaRupeeSign, FaInfoCircle, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";

const base_url = process.env.REACT_APP_BASE_URL;

const LiveAuctionSection = () => {
  const [auctions, setAuctions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchUserBids = async (page = 1) => {
    setLoading(true);
    setError("");

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Please login to view your bids");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${base_url}/api/live-auction/bidsPlacedByUser?page=${page}&limit=${pagination.limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "Failed to fetch your bids");

      // ✅ Use new response structure
      setAuctions(result.data.items || []);
      setPagination(result.data.pagination || {});
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserBids(pagination.page);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchUserBids(newPage);
  };

  if (loading)
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading your auction details...</p>
      </Container>
    );

  if (error)
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );

  if (auctions.length === 0)
    return (
      <Container className="text-center py-5">
        <FaInfoCircle size={48} className="text-muted mb-3" />
        <h4>No Auction Bids Found</h4>
        <p className="text-muted">
          You haven't placed any bids yet. Explore our auctions to get started!
        </p>
        <Link to="/auctions" className="btn btn-primary mt-3">
          Browse Auctions
        </Link>
      </Container>
    );

  return (
    <Container className="py-4">
      <Row>
        {auctions.map((auction) => (
          <Col md={6} lg={4} className="mb-4" key={auction.auction_id}>
            <Link 
              to={`/auctiondetails/${auction.auction_id}`}
              style={{ textDecoration: 'none' }}
            >
              <Card 
                className="h-100 shadow-sm auction-card card_floral floral-border"
                style={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  overflow: 'hidden',
                  borderRadius: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                }}
              >
                <div className="position-relative">
                  <Card.Img
                    variant="top"
                    src={auction.auction_details?.main_image || "https://via.placeholder.com/400x300?text=No+Image"}
                    alt={auction.auction_details?.title || "Auction Item"}
                    style={{
                      height: '200px',
                      objectFit: 'cover',
                      width: '100%'
                    }}
                  />
                  <Badge
                    bg={auction.user_message?.toLowerCase().includes("highest") ? "success" : "warning"}
                    className="position-absolute top-0 end-0 m-2 px-3 py-2"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {auction.user_message || "Active"}
                  </Badge>
                </div>
                <Card.Body>
                  <Card.Title className="h5 mb-2 text-truncate">
                    {auction.auction_details?.title || "Untitled Auction"}
                  </Card.Title>
                  <Card.Text className="text-muted small mb-3">
                    Auction ID: #{auction.auction_id}
                  </Card.Text>
                  
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <div className="text-muted small">Current Bid</div>
                      <div className="fw-bold text-dark">
                        <FaRupeeSign className="me-1 text-success" />
                        {auction.current_highest_bid || "N/A"}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-muted small">Your Bid</div>
                      <div className="fw-bold text-primary">
                        <FaRupeeSign className="me-1" />
                        {auction.user_highest_bid || "N/A"}
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                    <div>
                      {auction.user_message?.toLowerCase().includes("highest") ? (
                        <Badge bg="success" className="px-3 py-2">
                          Highest Bidder
                        </Badge>
                      ) : (
                        <Badge bg="secondary" className="px-3 py-2">
                          Outbid
                        </Badge>
                      )}
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/auctiondetails/${auction.auction_id}`;
                    }}>
                      View Details
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <Button
          variant="outline-secondary"
          disabled={pagination.page <= 1}
          onClick={() => handlePageChange(pagination.page - 1)}
        >
          Previous
        </Button>

        <div className="text-muted small">
          Page <strong>{pagination.page}</strong> of{" "}
          <strong>{pagination.totalPages}</strong> ({pagination.total} total)
        </div>

        <Button
          variant="outline-primary"
          disabled={pagination.page >= pagination.totalPages}
          onClick={() => handlePageChange(pagination.page + 1)}
        >
          Next
        </Button>
      </div>
    </Container>
  );
};

export default LiveAuctionSection;
